import { Component, ViewChild, TemplateRef } from "@angular/core";
import { BlockBaseComponent } from "../block-base/block-base.component";
import { TranslocoService } from "@ngneat/transloco";
import { StoreSelectorsService } from "../../../../services";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ChartEditorComponent } from "../../../../components/chart-editor/chart-editor.component";
import { IChartType } from "../../../../interfaces";
import { isNullOrUndef, Languages } from "../../../../common";
import { ITableListColumn, ITableListEvent } from "../../../../components/table-list-input/table-list-input.component";
import { IButtonBar } from "../../../../components/add-remove-bar/button-bar.component";
import { FieldConfig } from "../../../../interfaces/field.interface";
import * as _ from "lodash";
import { color } from "highcharts";

interface IChartTypeOption {
    type: IChartType;
    title: string;
    category: string;
    icon: string;
    rotation?: string;
}

const defaultLegendColor = "13113625";

@Component({
    selector: "app-scene-block-chart",
    templateUrl: "./block-chart.component.html",
    styleUrls: ["./block-chart.component.scss"],
})
export class BlockChartComponent extends BlockBaseComponent {
    @ViewChild("chartTypeSelectorDialog") chartTypeSelectorDialog: TemplateRef<any>;

    chartType: IChartType = "column";
    showLabel = false;
    showValue = false;
    showGrid = false;
    chartFilterText = "";
    viewMode: "grid" | "list" = "grid";
    source = 'fixed';

    tableColumns: Array<ITableListColumn> = [];
    buttons: Array<IButtonBar> = [];
    aiConfigInit: Array<FieldConfig> = [];
    currentChart: IChartTypeOption = null;

    private chartTypeDialogRef: MatDialogRef<any>;

    chartTypes: Array<IChartTypeOption> = [
        // Line Charts
        { type: "line", title: "Line Chart", category: "Basic", icon: "insights" },
        { type: "stackedLine", title: "Stacked Line", category: "Stacked", icon: "stacked_line_chart" },
        { type: "smoothLine", title: "Smooth Line", category: "Advanced", icon: "earbuds" },
        { type: "dottedLine", title: "Dotted Line", category: "Advanced", icon: "show_chart" },
        { type: "steppedLine", title: "Stepped Line", category: "Advanced", icon: "shortcut" },
        { type: "lineRace", title: "Line Race", category: "Advanced", icon: "ssid_chart" },

        // Area Charts
        { type: "area", title: "Area Chart", category: "Basic", icon: "broken_image" },
        { type: "stackedArea", title: "Stacked Area", category: "Stacked", icon: "area_chart" },
        { type: "gradientArea", title: "Gradient Area", category: "Area", icon: "gradient" },

        // Bar Charts
        { type: "column", title: "Column Chart", category: "Basic", icon: "equalizer" },
        { type: "bar", title: "Bar Chart", category: "Basic", icon: "equalizer", rotation: "90deg" },
        { type: "stackedColumn", title: "Stacked Column", category: "Stacked", icon: "stacked_bar_chart" },
        { type: "stackedHorizontalBar", title: "Stacked Horizontal Bar", category: "Stacked", icon: "stacked_bar_chart", rotation: "90deg" },
        { type: "roundedBar", title: "Rounded Bar", category: "Bar Variations", icon: "view_week" },
        { type: "negativeBar", title: "Negative Bar", category: "Bar Variations", icon: "align_vertical_center" },
        { type: "groupedBar", title: "Grouped Bar", category: "Bar Variations", icon: "table_chart", rotation: "180deg" },
        { type: "barRace", title: "Bar Race", category: "Animated", icon: "signal_cellular_alt" },
        { type: "pictorialBar", title: "Pictorial Bar", category: "Special Bar", icon: "insert_chart" },
        { type: "waterfall", title: "Waterfall Chart", category: "Special Bar", icon: "waterfall_chart" },

        // Pie Charts
        { type: "pie", title: "Pie Chart", category: "Circular", icon: "pie_chart" },
        { type: "doughnut", title: "Doughnut Chart", category: "Circular", icon: "donut_large" },
        { type: "sunburst", title: "Sunburst Chart", category: "Circular", icon: "wb_sunny" },
        { type: "rose", title: "Rose (Nightingale)", category: "Advanced", icon: "local_florist" },

        // Scatter Charts
        { type: "scatter", title: "Scatter Chart", category: "Basic", icon: "scatter_plot" },
        { type: "effectScatter", title: "Effect Scatter", category: "Advanced", icon: "workspaces" },
        { type: "bubble", title: "Bubble Chart", category: "Advanced", icon: "bubble_chart" },

        // Other Charts
        { type: "mixedLineBar", title: "Mixed Line & Bar", category: "Mixed", icon: "insert_chart_outlined" },
        { type: "gauge", title: "Gauge Chart", category: "Specialty", icon: "speed" },
        { type: "radar", title: "Radar Chart", category: "Specialty", icon: "radar" },
        { type: "funnel", title: "Funnel Chart", category: "Specialty", icon: "filter_alt" },
        { type: "polarBar", title: "Polar Bar Chart", category: "Polar", icon: "pie_chart_outline" },
        { type: "polarLine", title: "Polar Line Chart", category: "Polar", icon: "donut_small" },
        { type: "treemap", title: "Treemap Chart", category: "Hierarchical", icon: "grid_view" },
        { type: "heatmap", title: "Heatmap Chart", category: "Matrix", icon: "view_module" },
        { type: "candlestick", title: "Candlestick Chart", category: "Financial", icon: "candlestick_chart" },
        { type: "boxplot", title: "Boxplot Chart", category: "Statistical", icon: "ballot" },
        { type: "parallel", title: "Parallel Coordinates", category: "Multi-Dimensional", icon: "align_horizontal_left" },
        { type: "sankey", title: "Sankey Diagram", category: "Flow & Network", icon: "timeline" },
        { type: "graph", title: "Graph Network", category: "Flow & Network", icon: "hub" },
        { type: "liquidFill", title: "Liquid Fill", category: "Gauge", icon: "water_drop" },
        // { type: 'radialBar', title: 'Radial Bar', category: 'Radial', icon: 'donut_large' },
    ];

    filteredChartTypes: Array<IChartTypeOption> = [];

    constructor(private translocoService: TranslocoService, public override storeSelector: StoreSelectorsService, public override dialog: MatDialog) {
        super(storeSelector, dialog);
        this.filteredChartTypes = _.cloneDeep(this.chartTypes);
        this.initAiTableConfig();
    }

    initAiTableConfig() {
        this.tableColumns = [
            {
                inputType: "color",
                name: "color",
                maxStringSize: "10",
            },
            {
                inputType: "edit",
                name: "name",
                label: "legend",
                maxStringSize: "16",
            },
            {
                inputType: "edit",
                name: "description",
                maxStringSize: "50",
            },
        ];
        this.buttons = [
            {
                callBack: (v) => v,
                icon: "add",
                name: "add",
                disableByList: false,
                disabled: false,
                tooltip: this.translocoService.translate("general.add", {}, this.translocoService.getActiveLang()),
            },
            {
                callBack: (v) => v,
                icon: "remove",
                name: "remove",
                confirmRemove: this.translocoService.translate("toast.deleteConfirm", {}, this.translocoService.getActiveLang()),
                disableByList: true,
                disabled: false,
                tooltip: this.translocoService.translate("general.remove", {}, this.translocoService.getActiveLang()),
            },
        ];
    }

    override submit(formValues) {
        return super.submit(formValues, null, true);
    }

    submitAiConfig(formValues) {
        if (!Array.isArray(formValues.Prompt)) {
            formValues.Prompt = [formValues.Prompt];
        }
        return super.submit(formValues, null, true, true, this.aiConfigInit);
    }

    override componentInit() {
        super.componentInit();
        this.initProps();
    }

    override componentStoreUpdates() {
        super.componentStoreUpdates();
        this.checkAISource(this.sceneItem.Data[0]?.DataProvider?.[0]?.$.source);
        this.initProps();
        
        super.initApplyStoreToDynamic(true);
        super.initApplyStoreToDynamic(true, this.aiConfigInit);
    }

    override initRegisterDynamic() {
        this.uiConfigInit = [
            {
                type: "input",
                inputType: "color",
                label: this.translocoService.translate("ai.axisColor", {}, this.translocoService.getActiveLang()),
                name: "axisColor",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.axisColor",
            },
            {
                type: "checkbox",
                label: this.translocoService.translate("general.showLabel", {}, this.translocoService.getActiveLang()),
                name: "showLabel",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.showLabel",
            },
            {
                type: "checkbox",
                label: this.translocoService.translate("general.showValue", {}, this.translocoService.getActiveLang()),
                name: "showValue",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.showValue",
            },
            {
                type: "checkbox",
                label: this.translocoService.translate("general.showGrid", {}, this.translocoService.getActiveLang()),
                name: "showGrid",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.showGrid",
            },
            {
                type: "checkbox",
                label: this.translocoService.translate("general.showLegend", {}, this.translocoService.getActiveLang()),
                name: "showLegend",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.showLegend",
            },
            {
                type: "checkbox",
                label: this.translocoService.translate("general.showTooltip", {}, this.translocoService.getActiveLang()),
                name: "showTooltip",
                value: "0",
                ignoreMissingValue: true,
                storePath: "Data[0].Chart[0].$.showTooltip",
            },
        ];

        // Initialize AI configuration separately
        this.initAiConfig();
    }

    initAiConfig() {
        this.aiConfigInit = [
            {
                type: "select",
                label: this.translocoService.translate("ai.language", {}, this.translocoService.getActiveLang()),
                name: "language",
                value: "English",
                ignoreMissingValue: true,
                storePath: `Data[0].DataProvider[0].Ai[0].$.language`,
                options: Languages,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("ai.prompt", {}, this.translocoService.getActiveLang()),
                inputType: "textarea",
                name: "Prompt",
                ignoreMissingValue: true,
                storePath: `Data[0].DataProvider[0].Ai[0].Prompt`,
            },
            {
                type: "input",
                value: "",
                label: this.translocoService.translate("ai.series", {}, this.translocoService.getActiveLang()),
                inputType: "textarea",
                name: "Series",
                ignoreMissingValue: true,
                storePath: `Data[0].DataProvider[0].Ai[0].Series`,
            },
            {
                type: "input",
                label: "",
                name: "Fields",
                inputType: "tablelist",
                tableColumns: this.tableColumns,
                cloneVals: true,
                multiSelect: false,
                rememberSelection: false,
                allowFilter: false,
                disableSubmit: false,
                expandableEditor: true,
                enablePagination: false,
                allowTableOnDialog: true,
                buttonBarClassName: "blockJsonButtonBar",
                buttons: this.buttons,
                ignoreMissingValue: true,
                inputEditFormat: { name: "text", description: "text", color: "color" },
                inputEditRenamePath: [
                    { path: "$.name", key: "name" },
                    { path: "$.description", key: "description" },
                    { path: "$.color", key: "color" },
                ],
                storePath: `Data[0].DataProvider[0].Ai[0].Fields[0].Field`,
                callBackEvent: (v) => this.onTableListChangeCallBack(v),
                callBackValue: this.onCallBackValue(),
            },
        ];
    }

    private initProps() {
        this.chartType = (this.sceneItem.Data[0].Chart[0].$.type as IChartType) || "column";
        this.source = (this.sceneItem.Data[0].DataProvider?.[0]?.$?.source as string) || "fixed";
        this.currentChart = this.chartTypes.find(chart => chart.type === this.chartType);
    }

    onCallBackValue() {
        return (item, direction, index = null) => {
            const legendColors = this.sceneItem?.Data?.[0]?.Chart?.[0]?.Legend?.[0]?.colors || [];
            
            // Get the color for this index, or default to empty string
            const color = !isNullOrUndef(index) && index !== -1 && index < legendColors.length ? legendColors[index] : defaultLegendColor;

            switch (direction) {
                case "IN": {
                    item.$ = {
                        name: item?.$?.name,
                        description: item?.$.description,
                        color: color
                    };
                    return item;
                }
                case "OUT": {
                    return item;
                }
            }
        };
    }

    filter() {
        if (!this.chartFilterText.trim()) {
            this.filteredChartTypes = _.cloneDeep(this.chartTypes);
            return;
        }
        const filterLower = this.chartFilterText.toLowerCase().trim();
        this.filteredChartTypes = this.chartTypes.filter(
            (chart) => chart.title.toLowerCase().includes(filterLower) || chart.category.toLowerCase().includes(filterLower) || chart.type.toLowerCase().includes(filterLower),
        );
    }

    setViewMode(mode: "grid" | "list") {
        this.viewMode = mode;
    }

    toggleViewMode() {
        // Toggle between grid and list
        this.viewMode = this.viewMode === "grid" ? "list" : "grid";
    }

    get viewModeIcon(): string {
        return this.viewMode === "grid" ? "grid_view" : "view_list";
    }

    get viewModeLabel(): string {
        return this.viewMode === "grid" ? "Grid View" : "List View";
    }

    changeChartType(type: IChartType) {
        this.chartType = type;
        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: [
                {
                    key: "type",
                    value: type,
                    storePath: "Data[0].Chart[0].$",
                },
            ],
        });
    }

    onDataSourceChange(source: string) {
        this.source = source;
        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: [
                {
                    key: "source",
                    value: source,
                    storePath: "Data[0].DataProvider[0].$",
                },
            ],
        });
    }

    openChartTypeDialog() {
        this.chartTypeDialogRef = this.dialog.open(this.chartTypeSelectorDialog, {
            width: "600px",
            disableClose: false,
            autoFocus: false,
        });
    }

    selectChartTypeFromDialog(type: IChartType) {
        this.changeChartType(type);
        if (this.chartTypeDialogRef) {
            this.chartTypeDialogRef.close();
        }
    }

    private onTableListChangeCallBack(e: ITableListEvent) {
        if (e.currentAction === "drop" || e.currentAction === "removed" || e.currentAction === "rename") {
            // Extract colors from the values before they are stored
            const colors = e.values.map(item => item?.$?.color || defaultLegendColor);
            const fields = e.values.map(item => {
                const newItem = { ...item };
                if (newItem.$.color) {
                    delete newItem.$.color;
                }
                return newItem;
            });

            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [
                    {
                        key: "Field",
                        value: fields,
                        storePath: "Data[0].DataProvider[0].Ai[0].Fields[0]",
                    },
                    {
                        key: "colors",
                        value: colors,
                        storePath: "Data[0].Chart[0].Legend[0]",
                    },
                ],
            });
        } else if (e.currentAction === "added") {
            // Add new row default values
            const newItem = {
                $: {
                    name: "legend",
                    description: "describe for the legend",
                },
            };
            e.values.push(newItem);

            // Extract colors from the values
            const colors = e.values.map(item => item?.$?.color || defaultLegendColor);
            const fields = e.values.map(item => {
                const newItem = { ...item };
                if (newItem.$.color) {
                    delete newItem.$.color;
                }
                return newItem;
            });

            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [
                    {
                        key: "Field",
                        value: fields,
                        storePath: "Data[0].DataProvider[0].Ai[0].Fields[0]",
                    },
                    {
                        key: "colors",
                        value: colors,
                        storePath: "Data[0].Chart[0].Legend[0]",
                    },
                ],
            });
        }
    }

    openChartEditor() {
        // Get legend data from Data[0].Chart[0].Legend with proper null checks
        const legendArray = this.sceneItem.Data[0].Chart[0].Legend;
        const legendColors = legendArray && legendArray.length > 0 && legendArray[0].colors ? legendArray[0].colors : [];
        const legendTitles = legendArray && legendArray.length > 0 && legendArray[0].titles ? legendArray[0].titles : [];
        const chartData = this.sceneItem.Data[0].Chart[0].ChartData || [];

        // Combine legend colors and titles into single object
        const legendData = {
            colors: legendColors,
            titles: legendTitles,
        };

        const dialogRef = this.dialog.open(ChartEditorComponent, {
            width: "90vw",
            maxWidth: "1200px",
            data: {
                chartData: chartData,
                chartType: this.chartType,
                legendData: legendData,
            },
            disableClose: false,
            autoFocus: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.handleChartEditorResult(result);
            }
        });
    }

    private handleChartEditorResult(result: any) {
        if (!result) return;

        // Handle both old format (array) and new format (object with chartData and legendData)
        const chartData = Array.isArray(result) ? result : result.chartData;
        const legendData = Array.isArray(result) ? null : result.legendData;

        const updateValues = [
            {
                key: "ChartData",
                value: chartData,
                storePath: "Data[0].Chart[0]",
            },
        ];

        // Update legend data if provided
        if (legendData) {
            if (legendData.colors) {
                updateValues.push({
                    key: "colors",
                    value: legendData.colors,
                    storePath: "Data[0].Chart[0].Legend[0]",
                });
            }

            if (legendData.titles) {
                updateValues.push({
                    key: "titles",
                    value: legendData.titles,
                    storePath: "Data[0].Chart[0].Legend[0]",
                });
            }
        }

        // Update the scene with the new chart data and colors
        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: updateValues,
        });
    }

    checkAISource(source) {
        if (source === "ai") {
            if (!this.sceneItem.Data[0].DataProvider?.[0]?.Ai) {
                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: [
                        {
                            key: "Ai",
                            value: this.getDefaultAi(),
                            storePath: "Data[0].DataProvider[0]",
                            cloneValue: true,
                        },
                    ],
                });
            }
        }
    }

    private getDefaultAi() {
        return [
            {
                $: {
                    language: "English",
                },
                Prompt: [""],
                Series: [""],
                Fields: [
                    {
                        Field: [
                            {
                                $: {
                                    name: "legend",
                                    description: "describe for the legend",
                                },
                            },
                        ],
                    },
                ],
            },
        ];
    }

}
